// A person chosen from the directory, with a type-ahead search -- the Action
// Owner on the barrier form.
//
// The search is the API's, which asks Microsoft Graph as its own identity:
// this client holds no Graph permission, and the API would not take a person it
// had not looked up itself. So this sends what a person typed, shows who came
// back, and hands the chosen one's object id and name to the form.
//
// A name with no object id is one typed by hand before Action Owner came from
// the directory. It is shown as typed and marked as matching nobody, so the
// person reading it knows to pick somebody, rather than being mistaken for a
// person the directory vouches for or dropped from the screen.
//
// THE WIDGET IS A COMBOBOX in the ARIA sense: the input owns a listbox, the
// arrow keys move through it, Enter picks and Escape closes. Enter inside a
// form would otherwise submit the form, so it is taken only while the list is
// open on an option.

import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import * as api from '../api';
import type { DirectoryPerson } from '../types';

/** Two characters, because the API refuses fewer: one letter matches a large
 *  part of a tenant and says nothing about who was meant. */
export const MIN_SEARCH = 2;

/** How long typing has to pause before a search is sent, so that typing a name
 *  is one query rather than one per keystroke. */
export const SEARCH_DELAY_MS = 250;

export interface PersonValue {
  objectId: string | null;
  name: string | null;
}

export interface PeoplePickerProps {
  value: PersonValue;
  onChange: (next: PersonValue) => void;
  /** The id of the element naming the field, for `aria-labelledby`. */
  labelledBy: string;
  invalid?: boolean;
}

export function PeoplePicker({ value, onChange, labelledBy, invalid = false }: PeoplePickerProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [failure, setFailure] = useState<Error | null>(null);
  // Each search is numbered, and an answer that is not to the latest one is
  // dropped: a slow answer to "pa" must not replace a quick one to "pat".
  const latest = useRef(0);

  const term = query.trim();

  useEffect(() => {
    if (term.length < MIN_SEARCH) {
      latest.current += 1;
      setPeople([]);
      setSearching(false);
      setFailure(null);
      return;
    }
    const ticket = ++latest.current;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const found = await api.searchPeople(term);
        if (ticket !== latest.current) return;
        setPeople(found);
        setFailure(null);
        setActive(found.length > 0 ? 0 : -1);
      } catch (caught) {
        if (ticket !== latest.current) return;
        setPeople([]);
        setFailure(caught as Error);
      } finally {
        if (ticket === latest.current) setSearching(false);
      }
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [term]);

  function choose(person: DirectoryPerson) {
    onChange({ objectId: person.object_id, name: person.display_name ?? person.user_principal_name });
    setQuery('');
    setOpen(false);
  }

  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, people.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && active >= 0 && people[active]) {
        event.preventDefault();
        choose(people[active]);
      }
    } else if (event.key === 'Escape') {
      // Escape closes the list and goes no further, so a panel the picker sits
      // in stays open. With no list showing, it is the panel's to close.
      if (showList) event.stopPropagation();
      setOpen(false);
    }
  }

  const showList = open && term.length >= MIN_SEARCH;
  const unmatched = value.name !== null && value.objectId === null;

  return (
    <div className={invalid ? 'people-picker failed' : 'people-picker'}>
      {value.name !== null && (
        <div className={unmatched ? 'person-chip unmatched' : 'person-chip'}>
          <span className="person-name">{value.name}</span>
          {unmatched && <span className="person-note">Not matched to anyone in the directory. Search to choose the person.</span>}
          <button
            type="button"
            className="person-clear"
            aria-label={`Clear ${value.name}`}
            onClick={() => onChange({ objectId: null, name: null })}
          >
            ×
          </button>
        </div>
      )}

      <input
        type="text"
        role="combobox"
        aria-labelledby={labelledBy}
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        placeholder={value.name === null ? 'Search by name or email' : 'Search to change'}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={keyDown}
      />

      {showList && (
        <ul className="people-list" id={listId} role="listbox">
          {searching && people.length === 0 ? (
            <li className="people-status">Searching…</li>
          ) : failure ? (
            <li className="people-status failed">{failure.message}</li>
          ) : people.length === 0 ? (
            <li className="people-status">No one in the directory matches “{term}”.</li>
          ) : (
            people.map((person, index) => (
              <li
                key={person.object_id}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                className={index === active ? 'person-option active' : 'person-option'}
                // Mouse down rather than click: the input's blur closes the
                // list, and it fires before a click would land.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(person);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="person-name">{person.display_name ?? person.user_principal_name}</span>
                {person.user_principal_name && <span className="person-upn">{person.user_principal_name}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
